import { useEffect, useState } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import { settingsApi } from '../api/cms';
import { defaultSettings } from '../api/defaults';
import type { SiteSettings } from '../types';

const ColorSwatch = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
  <div>
    <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">{label}</label>
    <div className="flex gap-2">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg border border-white/20 overflow-hidden cursor-pointer relative">
        <input type="color" value={value} onChange={e => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
        <div className="w-full h-full" style={{ background: value }} />
      </div>
      <input value={value} onChange={e => onChange(e.target.value)}
        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white font-mono focus:outline-none focus:border-[#295CA8] transition-colors"
        placeholder="#000000" maxLength={7} />
    </div>
  </div>
);

export default function SettingsPage() {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    settingsApi.get().then(setSettings).catch(() => setSettings(defaultSettings));
  }, []);

  const set = <K extends keyof SiteSettings>(k: K, v: SiteSettings[K]) => setSettings(s => ({ ...s, [k]: v }));
  const setColor = (k: keyof SiteSettings['colors'], v: string) => setSettings(s => ({ ...s, colors: { ...s.colors, [k]: v } }));
  const setSocial = (k: keyof SiteSettings['social'], v: string) => setSettings(s => ({ ...s, social: { ...s.social, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await settingsApi.update(settings);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch { /**/ } finally { setSaving(false); }
  };

  const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input {...props} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
  );

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="bg-[#07090f] border border-white/10 rounded-xl p-6 space-y-4">
      <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider border-b border-white/10 pb-3">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Setări Web Site</h1>
          <p className="text-white/40 text-sm mt-1">Logo, culori, date contact, rețele sociale, tracking.</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-xs text-green-400 bg-green-500/10 px-3 py-1.5 rounded-lg border border-green-500/20">✓ Salvat</span>}
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            <Save size={16} /> {saving ? 'Se salvează...' : 'Salvează'}
          </button>
        </div>
      </div>

      {/* Logo */}
      <Section title="Logo & Identitate">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">URL Logo</label>
            <Input value={settings.logoUrl} onChange={e => set('logoUrl', e.target.value)} placeholder="https://..." />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Alt Logo</label>
            <Input value={settings.logoAlt} onChange={e => set('logoAlt', e.target.value)} />
          </div>
        </div>
        {settings.logoUrl && (
          <div className="h-20 bg-white/5 rounded-lg flex items-center justify-center border border-white/10">
            <img src={settings.logoUrl} alt={settings.logoAlt} className="max-h-16 max-w-full object-contain" />
          </div>
        )}
      </Section>

      {/* Colors */}
      <Section title="Paletă de Culori">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <ColorSwatch label="Primary" value={settings.colors.primary} onChange={v => setColor('primary', v)} />
          <ColorSwatch label="Secondary" value={settings.colors.secondary} onChange={v => setColor('secondary', v)} />
          <ColorSwatch label="Accent" value={settings.colors.accent} onChange={v => setColor('accent', v)} />
          <ColorSwatch label="Background" value={settings.colors.background} onChange={v => setColor('background', v)} />
          <ColorSwatch label="Nav Bg" value={settings.colors.navBg} onChange={v => setColor('navBg', v)} />
        </div>
        <div className="flex gap-2 mt-2">
          {Object.values(settings.colors).map((c, i) => (
            <div key={i} className="flex-1 h-8 rounded" style={{ background: c }} title={c} />
          ))}
        </div>
      </Section>

      {/* Contact */}
      <Section title="Date de Contact">
        <div>
          <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Telefoane</label>
          {settings.phones.map((ph, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <Input value={ph} onChange={e => {
                const phones = [...settings.phones];
                phones[i] = e.target.value;
                set('phones', phones);
              }} placeholder="+40 XXX XXX XXX" />
              <button onClick={() => set('phones', settings.phones.filter((_, j) => j !== i))}
                className="p-2.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-all">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button onClick={() => set('phones', [...settings.phones, ''])}
            className="flex items-center gap-1.5 text-xs text-[#4ab8ff] hover:underline mt-1">
            <Plus size={12} /> Adaugă telefon
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Email</label>
            <Input value={settings.email} onChange={e => set('email', e.target.value)} type="email" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Adresă</label>
            <Input value={settings.address} onChange={e => set('address', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Google Maps URL</label>
            <Input value={settings.mapUrl} onChange={e => set('mapUrl', e.target.value)} placeholder="https://maps.google.com/..." />
          </div>
        </div>
      </Section>

      {/* Social */}
      <Section title="Rețele Sociale">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(Object.keys(settings.social) as (keyof SiteSettings['social'])[]).map(k => (
            <div key={k}>
              <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">{k}</label>
              <Input value={settings.social[k]} onChange={e => setSocial(k, e.target.value)} placeholder="https://..." />
            </div>
          ))}
        </div>
      </Section>

      {/* Analytics */}
      <Section title="Tracking & Analytics">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Google Tag Manager ID</label>
            <Input value={settings.gtmId} onChange={e => set('gtmId', e.target.value)} placeholder="GTM-XXXXXXX" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Google Analytics 4 ID</label>
            <Input value={settings.ga4Id} onChange={e => set('ga4Id', e.target.value)} placeholder="G-XXXXXXXXXX" />
          </div>
        </div>
      </Section>

      {/* Google API Credentials */}
      <Section title="🔑 Google API — Credențiale (Date Trafic Real)">
        <div className="p-3 rounded-lg text-xs mb-4" style={{ background: 'rgba(74,184,255,0.06)', border: '1px solid rgba(74,184,255,0.15)' }}>
          <p className="text-white/50 leading-relaxed">
            Introduceți credențialele Google API pentru a activa datele reale de trafic în Dashboard.
            Obțineți credențialele din <strong className="text-[#4ab8ff]">Google Cloud Console → APIs & Services → Credentials</strong>.
            Sunt necesare: <strong className="text-white/70">GA4 Reporting API</strong> și un <strong className="text-white/70">Service Account</strong> cu acces la proprietatea GA4.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">GA4 Property ID</label>
            <Input
              value={settings.googleApi?.propertyId ?? ''}
              onChange={e => setSettings(s => ({ ...s, googleApi: { ...s.googleApi, propertyId: e.target.value } }))}
              placeholder="ex: 123456789"
            />
            <p className="text-xs text-white/25 mt-1">Găsit în GA4 Admin → Property Settings → Property ID</p>
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">OAuth Client ID</label>
            <Input
              value={settings.googleApi?.clientId ?? ''}
              onChange={e => setSettings(s => ({ ...s, googleApi: { ...s.googleApi, clientId: e.target.value } }))}
              placeholder="xxx.apps.googleusercontent.com"
            />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">OAuth Client Secret</label>
            <Input
              type="password"
              value={settings.googleApi?.clientSecret ?? ''}
              onChange={e => setSettings(s => ({ ...s, googleApi: { ...s.googleApi, clientSecret: e.target.value } }))}
              placeholder="GOCSPX-..."
            />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">OAuth Refresh Token</label>
            <Input
              type="password"
              value={settings.googleApi?.refreshToken ?? ''}
              onChange={e => setSettings(s => ({ ...s, googleApi: { ...s.googleApi, refreshToken: e.target.value } }))}
              placeholder="1//0..."
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Service Account JSON (alternativă recomandată)</label>
          <textarea
            value={settings.googleApi?.serviceAccountJson ?? ''}
            onChange={e => setSettings(s => ({ ...s, googleApi: { ...s.googleApi, serviceAccountJson: e.target.value } }))}
            placeholder='{"type":"service_account","project_id":"...","private_key":"-----BEGIN RSA...","client_email":"...@....iam.gserviceaccount.com",...}'
            rows={5}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white/70 placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors font-mono resize-y"
          />
          <p className="text-xs text-white/25 mt-1">Descărcați JSON-ul Service Account din Google Cloud Console → IAM → Service Accounts</p>
        </div>
      </Section>

      {/* Footer */}
      <Section title="Text Footer">
        <Input value={settings.footerText} onChange={e => set('footerText', e.target.value)} placeholder="© 2025 Companie..." />
      </Section>
    </div>
  );
}
